import { Link, useParams } from 'react-router-dom';
import BookingForm from './BookingForm.jsx';
import { PhoneIcon, WhatsAppIcon } from './ContactIcons.jsx';
import ServiceIcon, { getServiceIconType } from './ServiceIcon.jsx';
import { clinic, getTreatment } from '../data/site.js';

const serviceAccentClasses = ['blue', 'lime', 'purple', 'orange'];
const treatmentReasonBySlug = {
  preventive: 'checkup-cleaning',
  restorative: 'root-canal',
  cosmetic: 'aesthetic-dentistry',
  implants: 'implants',
  children: 'children',
  emergency: 'emergency'
};

export default function TreatmentPage() {
  const { slug } = useParams();
  const treatment = getTreatment(slug);

  if (!treatment) {
    return (
      <section className="blog-page-hero">
        <div className="container">
          <h1 className="heading-display">Treatment not found.</h1>
          <p className="text-body">The treatment page you requested could not be found.</p>
          <Link to="/#treatments" className="btn btn--primary">View treatments</Link>
        </div>
      </section>
    );
  }

  return (
    <div className={`landing-page landing-page--${treatment.slug}`}>
      <section className="treatment-hero">
        <div className="container">
          <div className="treatment-hero__inner">
            <div className="treatment-hero__content">
              <p className="caption treatment-hero__caption">{treatment.category}</p>
              <h1 className="heading-display">{treatment.title} in {clinic.locality}</h1>
              <p className="text-body treatment-hero__text">{treatment.hero} {treatment.summary} Available for patients in {clinic.locality} after a clinical assessment.</p>
              <div className="landing-hero__actions">
                <Link to="#booking" className="btn btn--primary">Book a {treatment.shortTitle.toLowerCase()} visit</Link>
                <a href={clinic.phoneHref} className="landing-hero__link landing-hero__link--with-icon" aria-label={`Call ${clinic.phoneDisplay}`}>
                  <PhoneIcon />
                  Call Now
                </a>
              </div>
              <p className="landing-hero__note">Treatment suitability is confirmed after a clinical assessment.</p>
            </div>
            <div className="treatment-hero__image">
              <img src={treatment.image} alt={treatment.imageAlt} loading="eager" />
            </div>
          </div>
        </div>
      </section>

      <section className="lp-proof-strip" aria-label="Treatment principles">
        <div className="container lp-proof-strip__inner">
          {treatment.proof.map(([title, text]) => (
            <div className="lp-proof" key={title}>
              <p className="lp-proof__title">{title}</p>
              <p className="lp-proof__text">{text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="lp-section lp-section--soft" id="services">
        <div className="container">
          <div className="lp-section-head">
            <div>
              <p className="caption">Treatment options</p>
              <h2 className="heading-2">{treatment.title} in {clinic.locality}</h2>
            </div>
            <div className="lp-section-head__intro">
              <p className="text-body">The services below are selected according to your symptoms, oral health, goals and clinical suitability.</p>
            </div>
          </div>
          <div className="lp-service-grid">
            {treatment.services.map(([title, description], index) => (
              <article className={`lp-service-card lp-service-card--${serviceAccentClasses[index % serviceAccentClasses.length]}`} key={title}>
                <div className="lp-service-card__icon" aria-hidden="true">
                  <ServiceIcon type={getServiceIconType(title, treatment.slug)} />
                </div>
                <div className="lp-service-card__content">
                  <span className="lp-service-card__number">{String(index + 1).padStart(2, '0')}</span>
                  <h3 className="lp-service-card__title">{title}</h3>
                  <p className="lp-service-card__description">{description}</p>
                </div>
                <div className="lp-service-card__footer">
                  <span className="lp-service-card__fit">{treatment.whoBenefits[index % treatment.whoBenefits.length]}</span>
                  <Link to="#booking" className="lp-service-card__cta">
                    Discuss <span aria-hidden="true">→</span>
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="lp-section lp-section--surface lp-section--compact lp-faq" id="faqs">
        <div className="container lp-faq__layout">
          <div className="lp-faq__heading">
            <p className="caption">Questions before booking</p>
            <h2 className="heading-2">Common questions about {treatment.shortTitle.toLowerCase()} care.</h2>
            <p className="text-body">General guidance only. A dentist can advise properly after assessing your mouth and health history.</p>
          </div>
          <div className="lp-faq__list">
            {treatment.faqs.map(([question, answer]) => (
              <details key={question}>
                <summary>{question}</summary>
                <p>{answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="lp-section lp-section--tint">
        <div className="container lp-decision__inner">
          <div className="lp-decision__copy">
            <p className="caption">Who benefits?</p>
            <h2 className="heading-2">{treatment.shortTitle} care matched to you.</h2>
            <p className="text-body">If any of these sound familiar, a consultation can help you understand the safest next step.</p>
          </div>
          <ul className="lp-checklist">
            {treatment.whoBenefits.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="lp-section lp-section--surface" id="process">
        <div className="container">
          <div className="lp-section-head">
            <div>
              <p className="caption">Your visit</p>
              <h2 className="heading-2">How your visit works</h2>
            </div>
            <div className="lp-section-head__intro">
              <p className="text-body">Each stage is designed to make the diagnosis, choices and tradeoffs easier to understand.</p>
            </div>
          </div>
          <div className="lp-process__steps">
            {treatment.process.map((step, index) => (
              <div className="lp-step" key={step.title}>
                <span className="lp-step__number">STEP {index + 1}</span>
                <p className="lp-step__title">{step.title}</p>
                <p>{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="lead-capture" id="booking">
        <div className="container lead-capture__inner">
          <div className="lead-capture__copy">
            <p className="caption">Take the next step</p>
            <h2 className="heading-2">Arrange your {treatment.shortTitle.toLowerCase()} appointment in {clinic.locality}.</h2>
            <p className="text-body">Tell us what you need help with. The clinic team will guide you to the right appointment type at the Varanasi clinic.</p>
            <div className="lead-capture__contact">
              <a href={clinic.phoneHref} className="lead-capture__contact-link" aria-label={`Call ${clinic.phoneDisplay}`}>
                <PhoneIcon />
                Call Now
              </a>
              <a href={clinic.whatsappHref} className="lead-capture__contact-link lead-capture__contact-link--whatsapp" target="_blank" rel="noreferrer">
                <WhatsAppIcon />
                WhatsApp
              </a>
            </div>
          </div>
          <BookingForm initialReason={treatmentReasonBySlug[treatment.slug] || ''} />
        </div>
      </section>

      <section className="lp-related">
        <div className="container lp-related__inner">
          <p className="caption">You may also need</p>
          <div className="lp-related__links">
            {treatment.related.map((relatedSlug) => {
              const related = getTreatment(relatedSlug);
              return related ? (
                <Link key={related.slug} to={`/treatments/${related.slug}/`}>
                  {related.title}
                </Link>
              ) : null;
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
